import { Color } from '@persona-id/design-tokens';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { formatAddress, formatDate } from 'dashboard/lib/utils/formatters';
import { IdentityRecordAddress } from 'dashboard/models/report/profile';
import { Paragraph } from 'design-system/components/Text';

export const ProfileAddressTimeline = ({ addresses }: { addresses: IdentityRecordAddress[] }) => {
  const [selectedAddress, setSelectedAddress] = useState(0);
  const hasSingleAddress = (addresses || []).length === 0;

  return (
    <MapAndAddresses>
      <AddressTimeline>
        {addresses.map((address, addressIdx) => {
          return (
            <AddressContainer
              key={`profile-address-${addressIdx}`}
              id={`profile-address-${addressIdx}`}
              first={addressIdx === 0}
              last={addressIdx === addresses.length - 1}
              type='button'
              selected={selectedAddress === addressIdx}
              onClick={() => setSelectedAddress(addressIdx)}
              removeLine={hasSingleAddress}
            >
              <div>
                {!hasSingleAddress && (
                  <Circle
                    fill='none'
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    viewBox='0 0 10 10'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <circle cx='5' cy='5' r='4' fill='currentColor' />
                  </Circle>
                )}
              </div>
              <div>
                {formatAddress({
                  addressStreet1: address['street-1'],
                  addressStreet2: address['street-2'],
                  addressCity: address.city,
                  addressSubdivision: address.subdivision,
                  addressPostalCode: address['postal-code'],
                  countryCode: address['country-code'],
                  color: 'primary',
                  size: 'sm',
                })}
                {(address['date-first-seen'] || address['date-last-seen']) && (
                  <Paragraph color='secondary' size='sm'>
                    {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                    {formatDateRange(address['date-first-seen'], address['date-last-seen'])}
                  </Paragraph>
                )}
              </div>
            </AddressContainer>
          );
        })}
      </AddressTimeline>
      <div>
        <Map address={addresses?.[selectedAddress]}></Map>
      </div>
    </MapAndAddresses>
  );
};

const formatDateRange = (first: string, last: string) => {
  if (first === last) {
    return formatDate(first);
  }

  return `${first ? formatDate(first) : 'unknown'} to ${last ? formatDate(last) : 'unknown'}`;
};

const MAP_MARKER =
  'M 13.333 6.788 h 0.625 h -0.625 Z M 8 14.667 l -0.352 0.516 a 0.625 0.625 0 0 0 0.704 0 L 8 14.667 Z m -5.333 -7.88 h -0.625 h 0.625 Z M 8 1.334 v 0.625 v -0.625 Z m 4.708 5.455 c 0 1.875 -1.192 3.694 -2.5 5.105 a 17.552 17.552 0 0 1 -2.51 2.222 l -0.04 0.028 a 0.436 0.436 0 0 1 -0.008 0.006 l -0.002 0.001 l 0.352 0.517 l 0.352 0.516 l 0.002 -0.001 a 0.246 0.246 0 0 1 0.004 -0.003 l 0.014 -0.01 l 0.05 -0.034 l 0.178 -0.13 a 18.812 18.812 0 0 0 2.525 -2.262 c 1.36 -1.467 2.833 -3.588 2.833 -5.955 h -1.25 Z M 8 14.667 l 0.352 -0.517 H 8.35 a 0.436 0.436 0 0 1 -0.009 -0.007 l -0.04 -0.028 a 15.81 15.81 0 0 1 -0.74 -0.567 a 17.557 17.557 0 0 1 -1.77 -1.655 c -1.307 -1.411 -2.5 -3.23 -2.5 -5.105 h -1.25 c 0 2.367 1.475 4.487 2.834 5.955 a 18.81 18.81 0 0 0 2.704 2.392 l 0.05 0.034 l 0.013 0.01 a 0.246 0.246 0 0 1 0.005 0.003 L 8 14.668 Z m -4.708 -7.88 c 0 -1.284 0.499 -2.514 1.384 -3.42 l -0.894 -0.873 a 6.143 6.143 0 0 0 -1.74 4.294 h 1.25 Z m 1.384 -3.42 A 4.649 4.649 0 0 1 8 1.959 V 0.708 a 5.899 5.899 0 0 0 -4.218 1.786 l 0.894 0.874 Z M 8 1.959 c 1.244 0 2.44 0.506 3.324 1.41 l 0.894 -0.874 A 5.899 5.899 0 0 0 8 0.708 v 1.25 Z m 3.324 1.41 a 4.893 4.893 0 0 1 1.384 3.42 h 1.25 a 6.143 6.143 0 0 0 -1.74 -4.294 l -0.894 0.874 Z M 9.375 6.667 c 0 0.76 -0.616 1.375 -1.375 1.375 v 1.25 a 2.625 2.625 0 0 0 2.625 -2.625 h -1.25 Z M 8 8.042 c -0.76 0 -1.375 -0.616 -1.375 -1.375 h -1.25 A 2.625 2.625 0 0 0 8 9.292 v -1.25 Z M 6.625 6.667 c 0 -0.76 0.616 -1.375 1.375 -1.375 v -1.25 a 2.625 2.625 0 0 0 -2.625 2.625 h 1.25 Z M 8 5.292 c 0.76 0 1.375 0.615 1.375 1.375 h 1.25 A 2.625 2.625 0 0 0 8 4.042 v 1.25 Z';
const Map = ({ address }: { address: IdentityRecordAddress }) => {
  const mapElementRef = useRef<HTMLDivElement>();
  const mapRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.Marker>();

  const fullAddress = [
    [address?.['street-1'], address?.['street-2']].filter(Boolean).join(' '),
    [address?.['city'], address?.['subdivision'], address?.['postal-code']]
      .filter(Boolean)
      .join(', '),
  ].join(', ');

  useEffect(() => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK') {
        const position = results?.[0].geometry.location;

        markerRef.current?.setMap(null);

        markerRef.current = new google.maps.Marker({
          map: mapRef.current,
          position,
          icon: {
            path: MAP_MARKER,
            fillColor: `${Color.CorePurple700}`,
            fillOpacity: 1,
            strokeWeight: 0,
            rotation: 0,
            scale: 2,
            anchor: new google.maps.Point(8, 8),
          },
        });

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        mapRef.current.panTo(position);
      }
    });
  }, [fullAddress]);

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    mapRef.current = new google.maps.Map(mapElementRef.current, {
      zoom: 14,
    });
  }, [mapElementRef]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return <GoogleMapsView id='profile-report__map' ref={mapElementRef} data-chromatic='ignore' />;
};

const GoogleMapsView = styled.div`
  height: 100%;
  flex-grow: 1;
`;

const MapAndAddresses = styled.div`
  display: grid;
  grid-template: auto 1fr / 1fr 1fr;
`;

const AddressTimeline = styled.div`
  overflow-y: scroll;
  height: 500px;
`;

const PADDING_SIZE = 24;
const ICON_SIZE = 10;
const ICON_COLUMN_SIZE = 30;

const AddressContainer = styled.button<{
  first?: boolean;
  last?: boolean;
  selected?: boolean;
  removeLine: boolean;
}>`
  position: relative;
  background-color: ${({ selected, theme }) =>
    selected ? `${Color.CorePurple100}` : `${theme.color.background.primary}`};
  display: grid;
  grid-template-columns: ${ICON_COLUMN_SIZE}px auto;
  text-align: left;
  width: 100%;
  padding: 20px ${PADDING_SIZE}px;

  &:after {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    left: ${PADDING_SIZE + ICON_SIZE / 2 - 0.5}px; // -0.5 pixel to account for line thickness
    bottom: ${({ first, last }) => {
      if (first) {
        return '-30%';
      } else if (last) {
        return '70%';
      } else {
        return 0;
      }
    }};
    background-color: ${({ theme }) => theme.color.border.secondary};
    overflow: clip;
  }

  ${({ removeLine }) =>
    removeLine &&
    css`
      &:after {
        width: 0;
      }
    `}

  &:hover {
    cursor: pointer;
    background-color: ${Color.CorePurple100};

    & svg {
      color: ${Color.CorePurple400};
      stroke: ${Color.CorePurple700};
    }

    &:after {
      background-color: ${Color.CorePurple400};
    }
  }

  ${({ selected }) =>
    selected &&
    css`
      & svg {
        color: ${Color.CorePurple400};
        stroke: ${Color.CorePurple700};
      }

      &:after {
        background-color: ${Color.CorePurple400};
      }
    `}
`;

const Circle = styled.svg<{ isSelected?: boolean }>`
  position: relative;
  z-index: 1; // Elevate svg above :after element
  stroke: ${({ isSelected, theme }) =>
    isSelected ? `${Color.CorePurple700}` : `${theme.color.text.primary}`};
  color: ${({ isSelected, theme }) =>
    isSelected ? `${Color.CorePurple400}` : `${theme.color.background.primary}`};
`;
